import React, { useEffect, useState } from "react";
import { FiChevronRight, FiUser, FiHash } from "react-icons/fi";
import { FaAddressCard, FaEnvelope, FaPhone, FaMapLocationDot } from "react-icons/fa6";
import GenderIcon from "../../../../components/GenderIcon";
import { translateClassName } from "../../../../utils/classroomUtils";
import { calculateAge, formatDateDMY, parseToDate } from "../../../../utils/dateUtils";
import {
  PRIVACY_CHANNELS,
  PRIVACY_VISIBILITY_OPTIONS,
  DEFAULT_VISIBILITY,
  buildInitialChannels,
} from "../../../../features/privacy/definitions";

const StatusPill = ({ ok, yes, no }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
      ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
    }`}
  >
    {ok ? yes : no}
  </span>
);

const DEFAULT_CHANNEL_LABELS = {
  website: "Allow on website",
  newsletter: "Allow in newsletter",
  socials: "Allow on social media",
  promotionalMaterials: "Allow in promotional materials",
  internalPublications: "Allow in internal publications",
  localMedia: "Allow in local media",
  videoRecordings: "Allow video recordings",
};

const DEFAULT_VISIBILITY_LABELS = {
  VISIBLE: "Visible",
  NOT_VISIBLE: "Not visible",
  UNSET: "No preference set",
};

const resolveVisibilityLabel = (value, t) => {
  const normalized = value || DEFAULT_VISIBILITY;
  const option = PRIVACY_VISIBILITY_OPTIONS.find(({ value: opt }) => opt === normalized);
  const fallback = DEFAULT_VISIBILITY_LABELS[normalized] || normalized;
  if (!option?.labelKey) return fallback;
  return t(option.labelKey, { defaultValue: fallback });
};

const maskBsn = (value) => {
  if (value === null || value === undefined) return "—";
  const raw = String(value).replace(/\s+/g, "");
  if (!raw) return "—";
  const masked = raw.replace(/.(?=.{3})/g, "*");
  const groups = masked.match(/.{1,3}/g) || [masked];
  return groups.join(" ");
};

const getDateLocale = () => {
  const stored = typeof window !== "undefined" ? window.localStorage?.getItem("i18nextLng") : "";
  if (stored?.startsWith("nl")) return "nl-NL";
  if (stored?.startsWith("en")) return "en-US";
  return typeof navigator !== "undefined" && navigator.language ? navigator.language : "en-US";
};

const formatDateTime = (value) => {
  const date = parseToDate(value);
  if (!date) return "—";
  return date.toLocaleString(getDateLocale(), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const startOfDay = (value) => {
  const date = parseToDate(value);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const getAssignmentStartDate = (assignment) =>
  assignment?.startDate ??
  assignment?.classroom?.startDate ??
  assignment?.schoolYear?.startDate ??
  assignment?.classroom?.schoolYear?.startDate;

const getAssignmentEndDate = (assignment) =>
  assignment?.endDate ??
  assignment?.classroom?.endDate ??
  assignment?.schoolYear?.endDate ??
  assignment?.classroom?.schoolYear?.endDate;

const isActiveClassroomAssignment = (assignment) => {
  if (!assignment) return false;
  if (assignment.isCurrent === true || assignment.current === true) return true;
  if (assignment.isCurrent === false || assignment.current === false) return false;

  const today = startOfDay(new Date());
  const startDate = startOfDay(getAssignmentStartDate(assignment));
  const endDate = startOfDay(getAssignmentEndDate(assignment));

  if (startDate && today < startDate) return false;
  if (endDate && today > endDate) return false;
  if (startDate || endDate) return true;

  return true;
};

const isUpcomingClassroomAssignment = (assignment) => {
  const today = startOfDay(new Date());
  const startDate = startOfDay(getAssignmentStartDate(assignment));
  return !!startDate && today < startDate;
};

const PrivacySummary = ({ t, state, onEdit, onRetry, expanded = false, onToggleExpanded }) => {
  const { data, loading, error } = state || {};
  const canToggle = !!state && typeof onToggleExpanded === "function" && !loading && !error;

  return (
    <div className="mt-1">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          className={`flex items-center gap-2 text-left focus:outline-none ${
            canToggle ? "cursor-pointer" : "cursor-default"
          }`}
          onClick={canToggle ? () => onToggleExpanded?.() : undefined}
          aria-expanded={canToggle ? expanded : undefined}
        >
          <FiChevronRight
            className={`h-5 w-5 text-emerald-600 transition-transform ${
              expanded ? "rotate-90" : ""
            } ${canToggle ? "" : "opacity-40"}`}
            aria-hidden="true"
          />
          <span className="font-semibold text-gray-800 dark:text-gray-100">
            {t("privacy.title", { defaultValue: "Privacy & Publishing" })}
          </span>
        </button>

        {!!data && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onEdit}
              className="text-sm text-emerald-700 dark:text-emerald-300 hover:underline"
            >
              {t("common.actions.edit", { defaultValue: "Edit" })}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {t("common.loading", { defaultValue: "Loading..." })}
        </div>
      ) : error && !data ? (
        <div className="mt-2 text-sm">
          <span className="text-red-600 dark:text-red-400">
            {t("privacy.load_error", { defaultValue: "Failed to load privacy preferences." })}
          </span>
          <button
            type="button"
            onClick={onRetry}
            className="ml-2 underline text-gray-700 dark:text-gray-300 hover:text-emerald-700 dark:hover:text-emerald-300"
          >
            {t("common.retry", { defaultValue: "Retry" })}
          </button>
        </div>
      ) : !data ? (
        <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
          <p className={expanded ? "mb-3" : undefined}>
            {t("privacy.no_preferences", {
              defaultValue: "No privacy preferences have been saved yet.",
            })}
          </p>
          {expanded && (
            <button type="button" onClick={onEdit} className="parent-form-button parent-form-button-submit">
              {t("privacy.set_now", { defaultValue: "Set now" })}
            </button>
          )}
        </div>
      ) : (
        <div className="mt-2 space-y-2 text-sm text-gray-700 dark:text-gray-300">
          {expanded && (
            <>
              {PRIVACY_CHANNELS.map(({ key, labelKey }) => (
                <div key={key} className="flex items-center justify-between">
                  <span>{t(labelKey, { defaultValue: DEFAULT_CHANNEL_LABELS[key] || key })}</span>
                  <StatusPill
                    ok={!!data?.[key]}
                    yes={t("common.yes", { defaultValue: "Yes" })}
                    no={t("common.no", { defaultValue: "No" })}
                  />
                </div>
              ))}

              <div className="flex items-center justify-between">
                <span>{t("privacy.visibility.title", { defaultValue: "Visibility" })}</span>
                <span className="font-semibold">{resolveVisibilityLabel(data?.visibility, t)}</span>
              </div>

              {data?.updatedAt && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t("privacy.last_updated", { defaultValue: "Last updated" })}: {formatDateTime(data.updatedAt)}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ChildInfoSummary = ({
  t,
  expanded = false,
  onToggle,
  birthDate,
  bsn,
  pendingBsn = null,
  isPending = false,
  showEdit = false,
  onEdit,
  isEditing = false,
  firstName = "",
  lastName = "",
  draftFirstName = "",
  draftLastName = "",
  onNameChange,
  draftBsn = "",
  onDraftChange,
  canEditBsn = true,
  pendingUpdate = false,
  onSave,
  onCancel,
  saving = false,
  errorMessage = "",
}) => {
  const hasDetails = Boolean(birthDate || bsn || pendingBsn);
  const canToggle = !isEditing && hasDetails;
  const displayBsn = maskBsn(bsn);
  const showBody = isEditing || expanded;

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          className={`flex items-center gap-2 text-left focus:outline-none ${
            canToggle ? "cursor-pointer" : "cursor-default"
          }`}
          onClick={canToggle ? onToggle : undefined}
          aria-expanded={canToggle ? expanded : undefined}
        >
          <FiChevronRight
            className={`h-5 w-5 text-emerald-600 transition-transform ${
              expanded ? "rotate-90" : ""
            } ${canToggle ? "" : "opacity-40"}`}
            aria-hidden="true"
          />
          <span className="font-semibold text-gray-800 dark:text-gray-100">
            {t("parentPortal.child_info.title", { defaultValue: "Personal details" })}
          </span>
        </button>

        {!isEditing && showEdit && (
          pendingUpdate ? (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-100">
              {t("parentPortal.child_info.pending_change", { defaultValue: "Change in progress" })}
            </span>
          ) : (
            <button type="button" onClick={onEdit} className="text-sm text-emerald-700 dark:text-emerald-300 hover:underline">
              {t("common.actions.edit", { defaultValue: "Edit" })}
            </button>
          )
        )}
      </div>

      {showBody && (
        isEditing ? (
          <div className="mt-2 space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t("parentPortal.child_info.name", { defaultValue: "Name" })}
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <div className="flex flex-1 items-center gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-sm focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-100 dark:border-gray-700 dark:bg-gray-800 dark:focus-within:border-emerald-500">
                  <FiUser className="text-gray-500 dark:text-gray-400" />
                  <input
                    type="text"
                    value={draftFirstName}
                    onChange={(e) => onNameChange?.("firstName", e.target.value)}
                    className="w-full bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none dark:text-gray-100"
                    placeholder={t("common.first_name", { defaultValue: "First name" })}
                  />
                </div>
                <div className="flex flex-1 items-center gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-sm focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-100 dark:border-gray-700 dark:bg-gray-800 dark:focus-within:border-emerald-500">
                  <FiUser className="text-gray-500 dark:text-gray-400" />
                  <input
                    type="text"
                    value={draftLastName}
                    onChange={(e) => onNameChange?.("lastName", e.target.value)}
                    className="w-full bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none dark:text-gray-100"
                    placeholder={t("common.last_name", { defaultValue: "Last name" })}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span>{t("parentPortal.date_birth", { defaultValue: "Date of birth" })}</span>
              <span>{birthDate || "—"}</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t("children.common.bsn", { defaultValue: "BSN" })}
              </label>
              {canEditBsn ? (
                <div className="mt-1 flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-sm focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-100 dark:border-gray-700 dark:bg-gray-800 dark:focus-within:border-emerald-500">
                  <FiHash className="text-gray-500 dark:text-gray-400" />
                  <input
                    type="text"
                    value={draftBsn}
                    onChange={(e) => onDraftChange?.(e.target.value)}
                    className="w-full bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none dark:text-gray-100"
                    placeholder={t("children.common.bsn", { defaultValue: "BSN" })}
                  />
                </div>
              ) : (
                <div className="mt-1 flex items-center justify-between gap-2 rounded-2xl border border-gray-200 bg-gray-100 px-3 py-2 text-gray-500 opacity-80 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <FiHash />
                    <span>{displayBsn || "—"}</span>
                  </div>
                  <span className="text-xs">
                    {t("parentPortal.child_info.bsn_locked", { defaultValue: "BSN is fixed" })}
                  </span>
                </div>
              )}
              {errorMessage && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errorMessage}</p>}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onCancel} className="parent-form-button parent-form-button-reset px-3 py-1 text-xs">
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="parent-form-button parent-form-button-submit disabled:opacity-60 px-3 py-1 text-xs"
              >
                {saving
                  ? t("common.saving", { defaultValue: "Saving..." })
                  : t("common.save", { defaultValue: "Save" })}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-2 space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <div className="flex items-center justify-between">
            <span>{t("parentPortal.date_birth", { defaultValue: "Date of birth" })}</span>
            <span>{birthDate || "—"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>{t("children.common.bsn", { defaultValue: "BSN" })}</span>
            <span className="flex items-center gap-2">
              <span>
                {isPending && pendingBsn ? "" : displayBsn}
              </span>
              {isPending && pendingBsn && (
                <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                  {t("parentPortal.child_info.pending_badge", { defaultValue: "Change in progress" })}
                </span>
              )}
            </span>
          </div>
          {pendingBsn && !isPending && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("parentPortal.child_info.pending_label", {
                  defaultValue: "New BSN in progress: {{value}}",
                  value: maskBsn(pendingBsn),
                })}
              </p>
            )}
            {!bsn && !pendingBsn && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t("parentPortal.child_info.missing", { defaultValue: "No additional details available." })}
              </p>
            )}
          </div>
        )
      )}
    </div>
  );
};

const ClassroomSummary = ({ t, expanded = false, onToggle, content }) => (
  <div className="mt-3">
    <div className="flex items-center justify-between gap-3">
      <button
        type="button"
        className={`flex items-center gap-2 text-left focus:outline-none ${
          content ? "cursor-pointer" : "cursor-default opacity-60"
        }`}
        onClick={content ? onToggle : undefined}
        aria-expanded={content ? expanded : undefined}
      >
        <FiChevronRight
          className={`h-5 w-5 text-emerald-600 transition-transform ${expanded ? "rotate-90" : ""}`}
          aria-hidden="true"
        />
        <span className="font-semibold text-gray-800 dark:text-gray-100">
          {t("parentPortal.child_info.class_title", { defaultValue: "Class information" })}
        </span>
      </button>
    </div>

    {expanded && content && (
      <div className="mt-2 space-y-2 text-sm text-gray-700 dark:text-gray-300">
        {content}
      </div>
    )}
  </div>
);

const PrivacyEditor = ({ t, initial, disabled, onCancel, onSave }) => {
  const [channels, setChannels] = useState(() => buildInitialChannels(initial));
  const [visibility, setVisibility] = useState(initial?.visibility ?? DEFAULT_VISIBILITY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const visibilityGroupName = React.useMemo(
    () => `privacy-visibility-${Math.random().toString(36).slice(2, 10)}`,
    []
  );

  useEffect(() => {
    setChannels(buildInitialChannels(initial));
    setVisibility(initial?.visibility ?? DEFAULT_VISIBILITY);
  }, [initial]);

  const dirty =
    PRIVACY_CHANNELS.some(({ key }) => !!channels[key] !== !!initial?.[key]) ||
    (initial?.visibility ?? DEFAULT_VISIBILITY) !== visibility;

  const handleSave = async () => {
    if (!dirty) return;
    setErr(null);
    setSaving(true);
    try {
      const payload = PRIVACY_CHANNELS.reduce(
        (acc, { key }) => ({ ...acc, [key]: !!channels[key] }),
        {}
      );
      const normalizedVisibility = visibility === "UNSET" ? null : visibility;
      const body = {
        ...payload,
        visibility: normalizedVisibility,
        ...(initial?.id ? { id: initial.id } : {}),
      };
      await onSave(body);
    } catch (e) {
      setErr(
        e?.response?.data?.message ||
          e.message ||
          t("privacy.save_error", { defaultValue: "Failed to save changes" })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 text-sm leading-5">
      <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-2">
        <legend className="px-1 text-xs font-semibold">
          {t("privacy.channels", { defaultValue: "Publication channels" })}
        </legend>
        {PRIVACY_CHANNELS.map(({ key, labelKey }) => (
          <label key={key} className="flex items-center gap-2 mt-1 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={!!channels[key]}
              onChange={(e) =>
                setChannels((prev) => ({
                  ...prev,
                  [key]: e.target.checked,
                }))
              }
              disabled={disabled || saving}
            />
            {t(labelKey, { defaultValue: DEFAULT_CHANNEL_LABELS[key] || key })}
          </label>
        ))}
      </fieldset>

      <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-2">
        <legend className="px-1 text-xs font-semibold">
          {t("privacy.visibility.title", { defaultValue: "Visibility" })}
        </legend>
        {PRIVACY_VISIBILITY_OPTIONS.map(({ value, labelKey }) => (
          <label key={value} className="flex items-center gap-2 mt-1 text-sm">
            <input
              type="radio"
              name={visibilityGroupName}
              className="h-4 w-4"
              checked={visibility === value}
              onChange={() => setVisibility(value)}
              disabled={disabled || saving}
            />
            {t(labelKey, { defaultValue: DEFAULT_VISIBILITY_LABELS[value] || value })}
          </label>
        ))}
      </fieldset>

      {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="parent-form-button parent-form-button-reset px-2 py-1 text-xs"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={disabled || saving || !dirty}
          className="parent-form-button parent-form-button-submit disabled:opacity-60 px-2 py-1 text-xs"
        >
          {saving
            ? t("common.saving", { defaultValue: "Saving..." })
            : t("common.save", { defaultValue: "Save" })}
        </button>
      </div>
    </div>
  );
};

const ChildrenTab = ({
  t,
  visibleChildren,
  expandedChildId,
  setExpandedChildId,
  privacyByChild,
  loadPrivacyFor,
  togglePrivacyExpanded,
  startEditPrivacy,
  cancelEditPrivacy,
  savePrivacyFor,
  loadClassroomsForChild,
  classroomsByChild,
  childInfoExpanded,
  toggleChildInfoExpanded,
  startChildInfoEdit,
  childInfoEdit,
  updateChildInfoDraft,
  cancelChildInfoEdit,
  submitChildInfoEdit,
  addMode,
  setAddMode,
  newChild,
  setNewChild,
  handleNewChildChange,
  handleCreateChild,
  isWithdrawn,
  isDeceased,
  pendingChildren,
  pendingChildUpdateSet = new Set(),
}) => {
  const [teacherContactOpenFor, setTeacherContactOpenFor] = useState(null);

  const handleToggleChild = async (child) => {
    const next = expandedChildId === child.id ? null : child.id;
    setExpandedChildId(next);

    if (next) {
      if (child.pending && (!child.entityId || String(child.id).startsWith("pending-"))) {
        return;
      }
      if (!privacyByChild[child.id]?.data && !privacyByChild[child.id]?.loading) {
        loadPrivacyFor(child.id);
      }
      if (!classroomsByChild[child.id]?.data && !classroomsByChild[child.id]?.loading) {
        await loadClassroomsForChild(child.id);
      }
    }
  };

  const renderClassroomInfo = (child) => {
    const clsState = classroomsByChild[child.id] || {};
    if (clsState.loading) {
      return (
        <p>
          <strong>{t("parentPortal.class_or_waiting", { defaultValue: "Class" })}:</strong> {t("common.loading", { defaultValue: "Loading..." })}
        </p>
      );
    }
    if (clsState.error) {
      return (
        <p>
          <strong>{t("parentPortal.class_or_waiting", { defaultValue: "Class" })}:</strong> {t("parentPortal.classrooms_load_error", { defaultValue: "Could not load class information" })}
        </p>
      );
    }

    const raw = clsState.data;
    const assignments = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const current = assignments.find(isActiveClassroomAssignment) || null;
    const hasUpcomingPlacement = assignments.some(isUpcomingClassroomAssignment);

    if (!current) {
      if (hasUpcomingPlacement) {
        return (
          <>
            <p>
              <strong>{t("parentPortal.status", { defaultValue: "Status" })}:</strong>{" "}
              {t("parentPortal.placed_next_school_year", { defaultValue: "Placed for next school year" })}
            </p>
            <p>{t("parentPortal.not_started_yet", { defaultValue: "Not started yet" })}</p>
          </>
        );
      }
      if (child.waiting) {
        return (
          <p>
            <strong>{t("parentPortal.waiting", { defaultValue: "Wachtlijst" })}</strong>
          </p>
        );
      }
      return (
        <p>
          <strong>{t("parentPortal.not_assigned", { defaultValue: "Not assigned yet" })}</strong>
        </p>
      );
    }

    const className =
      current?.classroom?.className ??
      current?.className ??
      current?.classroomName ??
      current?.name ??
      "";

    const teacherObj =
      current.teacher ||
      (Array.isArray(current.teachers) ? current.teachers[0] : null) ||
      current.classroom?.primaryTeacher ||
      (Array.isArray(current.classroom?.teachers) ? current.classroom.teachers[0] : null) ||
      null;

    const teacherId =
      teacherObj?.id ||
      teacherObj?.uuid ||
      teacherObj?.teacherId ||
      current.teacherId ||
      current.classroom?.primaryTeacherId ||
      null;

    const teacherContact = {
      phone: current.teacherPhone || teacherObj?.phone,
      email: current.teacherEmail || teacherObj?.email,
      addressLine: current.teacherAddress || teacherObj?.address || teacherObj?.street,
      zipCode: current.teacherZipCode,
      city: current.teacherCity,
    };

    const teacherTeamNames = Array.isArray(current.teachers)
      ? current.teachers
          .map((teacher) => [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ").trim())
          .filter(Boolean)
      : [];
    const teacherFullName =
      (teacherTeamNames.length > 0 && teacherTeamNames.join(", ")) ||
      ([current.teacherFirstName, current.teacherLastName].filter(Boolean).join(" ").trim()) ||
      (teacherObj && [teacherObj.firstName, teacherObj.lastName].filter(Boolean).join(" ")) ||
      null;

    const addressLine = teacherContact.addressLine || teacherContact.address || teacherContact.street || "";
    const locationLine = [teacherContact.zipCode || teacherContact.postalCode, teacherContact.city].filter(Boolean).join(" ");
    const tooltipLines = [
      addressLine && addressLine.trim(),
      locationLine && locationLine.trim(),
      teacherContact.email && teacherContact.email.trim(),
      teacherContact.phone && teacherContact.phone.trim(),
    ].filter(Boolean);
    const hasSharedDetails = tooltipLines.length > 0;
    const teacherHasContact = teacherFullName || teacherId;
    const isContactOpen = teacherContactOpenFor === child.id;
    const teacherContactLines = hasSharedDetails
      ? tooltipLines
      : [t("parentPortal.teacher_no_shared_contact", { defaultValue: "Teacher has not shared contact details" })];

    const addressValue = [addressLine, locationLine].filter(Boolean).join("\n");

    return (
      <>
        <p>
          <strong>{t("parentPortal.class", { defaultValue: "Class" })}:</strong> {translateClassName(className) || t("parentPortal.not_assigned", { defaultValue: "Not assigned yet" })}
        </p>
        {teacherHasContact && (
          <div className="space-y-1">
            <p className="inline-flex items-center gap-2 align-middle">
              <strong>{t("parentPortal.teacher", { defaultValue: "Teacher" })}:</strong> {teacherFullName || "—"}
              {hasSharedDetails && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/70 px-2.5 py-0.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-white dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-slate-500"
                  onClick={() =>
                    setTeacherContactOpenFor((prev) => (prev === child.id ? null : child.id))
                  }
                >
                  <FaAddressCard className="text-emerald-600" />
                  {isContactOpen
                    ? t("parentPortal.teacher_contact_hide", { defaultValue: "Hide" })
                    : t("parentPortal.teacher_contact_show", { defaultValue: "Contact" })}
                </button>
              )}
            </p>
            {isContactOpen && hasSharedDetails && (
              <div className="mt-2 w-full max-w-xl rounded-2xl border border-emerald-200/40 bg-gradient-to-br from-white/70 to-emerald-50/30 p-3 text-sm text-slate-800 shadow-sm dark:border-emerald-700/40 dark:from-slate-900/50 dark:to-emerald-900/20 dark:text-slate-100">
                <div className="grid gap-1 sm:grid-cols-[1.2fr_1fr] sm:gap-x-3 sm:gap-y-1">
                  {addressValue ? (
                    <div className="flex items-start gap-2 sm:row-span-2">
                      <FaMapLocationDot className="mt-0.5 text-base text-emerald-600" />
                      <span className="whitespace-pre-line leading-snug">{addressValue}</span>
                    </div>
                  ) : null}
                  {teacherContact.email ? (
                    <div className="flex items-center gap-2">
                      <FaEnvelope className="text-base text-emerald-600" />
                      <span className="leading-snug">{teacherContact.email}</span>
                    </div>
                  ) : null}
                  {teacherContact.phone ? (
                    <div className="flex items-center gap-2">
                      <FaPhone className="text-base text-emerald-600" />
                      <span className="leading-snug">{teacherContact.phone}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <div className="parent-glass-card text-gray-900 dark:text-gray-100 space-y-2">
        {visibleChildren.length > 0 ? (
          <ul className="list-disc pl-4 space-y-2">
            {visibleChildren.map((child) => {
              const isExpanded = expandedChildId === child.id;
              const age = calculateAge(child.dateBirth);
              const ageLabel = Number.isFinite(age) ? age : "—";
              const birthDate = parseToDate(child.dateBirth);
              const withdrawnDate = formatDateDMY(child.withdrawnAt);
              const pendingMatch = Array.isArray(pendingChildren)
                ? pendingChildren.find(
                    (pending) =>
                      (pending.entityId && pending.entityId === child.id) ||
                      pending.id === child.id
                  )
                : undefined;
              const pendingBsnValue = child.pendingBsn || pendingMatch?.payloadData?.bsn || null;
              const hasPendingBsn = Boolean(
                pendingMatch &&
                  pendingMatch.action === "UPDATE" &&
                  (pendingMatch.status === "PENDING" || pendingMatch.pending) &&
                  pendingBsnValue
              );
              const isBsnPending = Boolean(hasPendingBsn || pendingBsnValue);
              const isPendingNewChild =
                !!child.pending &&
                (!pendingMatch?.entityId ||
                  pendingMatch?.action === "CREATE" ||
                  String(child.id).startsWith("pending-"));

              const birthDateLabel = birthDate
                ? birthDate.toLocaleDateString(getDateLocale(), {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : null;

              return (
                <li key={child.id} className="flex flex-col">
                  <div
                    onClick={() => handleToggleChild(child)}
                    className={`flex items-center gap-2 hover:text-emerald-700 dark:hover:text-emerald-300 transition cursor-pointer ${
                      isWithdrawn(child) ? "opacity-60" : ""
                    }`}
                  >
                    <FiChevronRight
                      className={`h-5 w-5 text-emerald-600 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                      aria-hidden="true"
                    />
                    <GenderIcon gender={child.gender} />
                    <span className="flex flex-wrap items-center gap-2">
                      <span>
                        {child.firstName} {child.lastName} (<strong>{ageLabel}</strong>)
                      </span>
                      {pendingChildUpdateSet.has(String(child.id)) && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/50 dark:text-amber-100">
                          {t("parentPortal.child_info.pending_change", { defaultValue: "Change in progress" })}
                        </span>
                      )}
                      {child.pending && (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                          {t("parentPortal.pending")}
                        </span>
                      )}
                      {child.rejected && (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-800 dark:text-red-100">
                          {t("parentPortal.rejected")}
                        </span>
                      )}
                      {child.waiting && !isWithdrawn(child) && (
                        <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800 dark:bg-orange-800 dark:text-orange-100">
                          {t("parentPortal.waiting")}
                        </span>
                      )}
                      {isWithdrawn(child) && withdrawnDate && (
                        <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                          {t("parentPortal.withdrawn_since", { defaultValue: "Withdrawn since {{date}}", date: withdrawnDate })}
                        </span>
                      )}
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="ml-7 mt-2 space-y-4 border-l-2 border-emerald-200/60 pl-4 dark:border-emerald-500/30">
                      {isPendingNewChild ? (
                        <div className="rounded-2xl border border-amber-100/70 bg-amber-50/70 px-4 py-3 text-sm text-amber-900 dark:border-amber-800/40 dark:bg-amber-900/30 dark:text-amber-100">
                          {t("parentPortal.pending_child_notice", {
                            defaultValue: "This child is awaiting approval. Details will be available once the request is approved.",
                          })}
                        </div>
                      ) : (
                        <>
                          <ClassroomSummary
                            t={t}
                            expanded={!!childInfoExpanded[`${child.id}-class`]}
                            onToggle={() =>
                              toggleChildInfoExpanded(`${child.id}-class`)
                            }
                            content={renderClassroomInfo(child)}
                          />

                          {(() => {
                            const editState = childInfoEdit[child.id] || {};
                            const hasPendingUpdate = pendingChildUpdateSet.has(String(child.id));
                            const showEditButton = !isWithdrawn(child) && !child.pending && !hasPendingUpdate;

                            return (
                              <ChildInfoSummary
                                t={t}
                                expanded={!!childInfoExpanded[child.id]}
                                onToggle={() => toggleChildInfoExpanded(child.id)}
                                birthDate={birthDateLabel}
                                bsn={child.bsn || null}
                                pendingBsn={pendingBsnValue}
                                isPending={isBsnPending}
                                showEdit={showEditButton}
                                onEdit={() => startChildInfoEdit(child)}
                                isEditing={!!editState.editing}
                                pendingUpdate={hasPendingUpdate}
                                canEditBsn={!child.bsn}
                                firstName={child.firstName || ""}
                                lastName={child.lastName || ""}
                                draftFirstName={editState.draftFirstName || child.firstName || ""}
                                draftLastName={editState.draftLastName || child.lastName || ""}
                                draftBsn={editState.draftBsn || ""}
                                onDraftChange={(value) => updateChildInfoDraft(child.id, "bsn", value)}
                                onNameChange={(field, value) => updateChildInfoDraft(child.id, field, value)}
                                onSave={() => submitChildInfoEdit(child)}
                                onCancel={() => cancelChildInfoEdit(child.id)}
                                saving={!!editState.saving}
                                errorMessage={editState.error}
                              />
                            );
                          })()}

                          <div>
                            {privacyByChild[child.id]?.editing ? (
                              <>
                                <div className="flex items-center justify-between mb-2">
                                  <h5 className="font-semibold text-gray-800 dark:text-gray-100">
                                    {t("privacy.title", { defaultValue: "Privacy & Publishing" })}
                                  </h5>
                                </div>

                                <PrivacyEditor
                                  t={t}
                                  initial={privacyByChild[child.id]?.data}
                                  disabled={false}
                                  onCancel={() => cancelEditPrivacy(child.id)}
                                  onSave={(payload) => savePrivacyFor(child.id, payload)}
                                />
                              </>
                            ) : (
                              <PrivacySummary
                                key={child.id}
                                t={t}
                                state={privacyByChild[child.id]}
                                onEdit={async () => {
                                  startEditPrivacy(child.id);
                                  if (!privacyByChild[child.id]?.data && !privacyByChild[child.id]?.loading) {
                                    await loadPrivacyFor(child.id);
                                  }
                                }}
                                onRetry={() => loadPrivacyFor(child.id)}
                                expanded={!!privacyByChild[child.id]?.expanded}
                                onToggleExpanded={() => togglePrivacyExpanded(child.id)}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="italic text-gray-500 dark:text-gray-400">{t("parentPortal.no_children")}</p>
        )}
      </div>

      {addMode ? (
        <div className="parent-glass-card mt-2 space-y-2 text-left text-gray-900 dark:text-gray-100">
          <h4 className="font-semibold mb-1">
            {t("parentPortal.create_child_title", { defaultValue: "Register new child" })}
          </h4>

          <div>
            <label className="block text-sm font-medium">{t("parentPortal.first_name")}</label>
            <input
              type="text"
              name="firstName"
              value={newChild.firstName}
              onChange={handleNewChildChange}
              className="w-full p-2 border rounded bg-white text-gray-900 placeholder-gray-400 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 dark:border-gray-600"
              placeholder={t("parentPortal.first_name")}
            />
          </div>

          <div>
            <label className="block text-sm font-medium">{t("parentPortal.last_name")}</label>
            <input
              type="text"
              name="lastName"
              value={newChild.lastName}
              onChange={handleNewChildChange}
              className="w-full p-2 border rounded bg-white text-gray-900 placeholder-gray-400 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 dark:border-gray-600"
              placeholder={t("parentPortal.last_name")}
            />
          </div>

          <div>
            <label className="block text-sm font-medium">{t("parentPortal.date_birth")}</label>
            <input
              type="date"
              name="dateBirth"
              value={newChild.dateBirth}
              onChange={handleNewChildChange}
              className="w-full p-2 border rounded bg-white text-gray-900 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium">{t("parentPortal.gender")}</label>
            <select
              name="gender"
              value={newChild.gender}
              onChange={handleNewChildChange}
              className="w-full p-2 border rounded bg-white text-gray-900 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            >
              <option value="">{t("children.common.select_gender")}</option>
              <option value="MALE">{t("children.common.gender_male")}</option>
              <option value="FEMALE">{t("children.common.gender_female")}</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-3">
            <button
              onClick={() => {
                setAddMode(false);
                setNewChild({ firstName: "", lastName: "", dateBirth: "", gender: "" });
              }}
              className="parent-form-button parent-form-button-reset"
              type="button"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={handleCreateChild}
              type="button"
              disabled={!newChild.firstName || !newChild.lastName || !newChild.dateBirth || !newChild.gender}
              className="parent-form-button parent-form-button-submit disabled:opacity-60"
            >
              {t("parentPortal.create_child")}
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAddMode(true)} className="mt-3 parent-form-button parent-form-button-submit">
          {t("parentPortal.buttons.add_child")}
        </button>
      )}
    </>
  );
};

export default ChildrenTab;
